import { useState, useEffect } from "react";
import "./Enquiry.css";

export default function Enquiry() {
  const [enquiries, setEnquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Pagination states from API
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Fetch enquiries from API with pagination
  const fetchEnquiries = async (page = 1, limit = 10, search = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (search) {
        params.append('search', search);
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/contact.php?${params}`,
        {
          method: "GET",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${process.env.REACT_APP_API_KEY}`
          }
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const response = await res.json();

      // Handle API response structure
      if (response.success) {
        const mappedData = response.data.map((item) => ({
          id: item.id,
          fullName: item.name,
          email: item.email,
          phone: item.phone,
          subject: item.subject,
          message: item.message,
          createdAt: item.created_at,
          status: "New"
        }));

        setEnquiries(mappedData);
        
        // Update pagination data from API
        if (response.pagination) {
          setTotalRecords(response.pagination.totalRecords);
          setTotalPages(response.pagination.totalPages);
          setCurrentPage(response.pagination.currentPage);
          setItemsPerPage(response.pagination.limit);
        }
      } else {
        setEnquiries([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Failed to fetch enquiries:", err);
      setEnquiries([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries(currentPage, itemsPerPage, searchTerm);
  }, [currentPage, itemsPerPage]);

  // Debounced search effect
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 on search
      fetchEnquiries(1, itemsPerPage, searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedEnquiry(null);
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = Number(e.target.value);
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Calculate display range
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <div className="enquiry-admin">
      <div className="enquiry-header">
        <h2><span>📧</span> Enquiry Management</h2>
        <p className="subtitle">
          View all contact form submissions from visitors (Read-Only)
        </p>

        <div className="header-controls">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search by name, email, phone, subject or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="items-per-page">
            <label>Show:</label>
            <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
        </div>

        <div className="results-info">
          {totalRecords > 0 ? (
            <p>
              Showing {startIndex} to {endIndex} of {totalRecords} enquiries
            </p>
          ) : (
            <p>No enquiries found</p>
          )}
        </div>
      </div>

      <div className="enquiry-list-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading enquiries...</p>
          </div>
        ) : enquiries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>No enquiries found</p>
            {searchTerm && (
              <button 
                className="clear-search-btn"
                onClick={() => setSearchTerm("")}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="enquiry-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Subject</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((enquiry) => (
                    <tr key={enquiry.id}>
                      <td>#{enquiry.id}</td>
                      <td>{formatDate(enquiry.createdAt)}</td>
                      <td>{enquiry.fullName}</td>
                      <td>{enquiry.email}</td>
                      <td>{enquiry.phone}</td>
                      <td>{enquiry.subject}</td>
                      <td className="truncate">{enquiry.message}</td>
                      <td>
                        <span className="status new">{enquiry.status}</span>
                      </td>
                      <td>
                        <button onClick={() => handleViewDetails(enquiry)}>
                          👁️ View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <button
                  className="pagination-btn"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1 || loading}
                >
                  ← Previous
                </button>

                <div className="pagination-numbers">
                  {getPageNumbers().map((page) => (
                    <button
                      key={page}
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                      disabled={loading}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || loading}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedEnquiry && (
        <div className="modal-overlay">
          <div className="modal-container view-modal">
            <div className="modal-header">
              <h3>Enquiry Details - #{selectedEnquiry.id}</h3>
              <button className="close-btn" onClick={closeViewModal}>×</button>
            </div>

            <div className="modal-body">
              <p><strong>ID:</strong> #{selectedEnquiry.id}</p>
              <p><strong>Name:</strong> {selectedEnquiry.fullName}</p>
              <p><strong>Email:</strong> {selectedEnquiry.email}</p>
              <p><strong>Phone:</strong> {selectedEnquiry.phone}</p>
              <p><strong>Subject:</strong> {selectedEnquiry.subject}</p>
              <p><strong>Message:</strong> {selectedEnquiry.message}</p>
              <p><strong>Status:</strong> {selectedEnquiry.status}</p>
              <p><strong>Date:</strong> {formatDate(selectedEnquiry.createdAt)}</p>
            </div>

            <div className="modal-footer">
              <button className="btn-close" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}